import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import moment from 'moment';
import ReactDatePicker from '../../react-datepicker-compat';
import 'react-datepicker/dist/react-datepicker.css';

/** 我的账户页下拉选项 */
export type MyAccountSelectOption = {
    /** 选项值 */
    value: string;
    /** 选项文案 */
    label: string;
};

/** 我的账户页专用输入框类型 */
type MyAccountInputType = 'INPUT' | 'SELECT' | 'DATE' | 'PASSWORD';

/** 我的账户页专用输入框参数 */
type MyAccountFormInputProps = {
    /** 左侧标签文案 */
    label: string;
    /** 当前值 */
    value: string;
    /** 输入类型 */
    inputType?: MyAccountInputType;
    /** 下拉选项 */
    options?: MyAccountSelectOption[];
    /** 日期最大值 */
    maxDate?: Date;
    /** 是否禁用 */
    disabled?: boolean;
    /** 值变化回调 */
    onChange: (value: string) => void;
};

/**
 * 我的账户页专用灰底输入框（左标签右输入、无边框）
 * 仅用于 my-account 页面，避免影响全局公共输入组件。
 */
export default function MyAccountFormInput(props: MyAccountFormInputProps) {
    // 输入类型，默认普通输入
    const { label, value, inputType = 'INPUT', options = [], maxDate, disabled = false, onChange } = props;
    // 日期值：字符串格式转 Date，供日期选择器使用
    const dateValue = React.useMemo(() => {
        if (!value) return null;
        const parsedDate = moment(value, moment.HTML5_FMT.DATETIME_LOCAL_MS, true);
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    }, [value]);

    /**
     * 处理日期变化并回写为接口兼容格式（带毫秒）
     * @param nextDate - 选择后的日期
     */
    const handleDateChange = (nextDate: Date | null) => {
        const formattedDate = nextDate ? moment(nextDate).format(moment.HTML5_FMT.DATETIME_LOCAL_MS) : '';
        onChange(formattedDate);
    };

    return (
        <Box className='my-account-inline-field'>
            <Box className='my-account-inline-field__label'>
                <Typography
                    variant='body1'
                    component='div'
                    className='my-account-inline-field__label-text'
                    title={label}
                    sx={{ color: 'var(--text-dark)' }}
                >
                    {label}
                </Typography>
            </Box>
            <Box className='my-account-inline-field__control'>
                {inputType === 'DATE' ? (
                    <ReactDatePicker
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        selected={dateValue}
                        onChange={handleDateChange}
                        dateFormat='yyyy/MM/dd'
                        maxDate={maxDate}
                        disabled={disabled}
                        customInput={
                            <TextField
                                fullWidth
                                size='small'
                                value={value}
                                inputProps={{ readOnly: true, title: value || '' }}
                                variant='outlined'
                            />
                        }
                    />
                ) : (
                    <TextField
                        fullWidth
                        size='small'
                        select={inputType === 'SELECT'}
                        type={inputType === 'PASSWORD' ? 'password' : 'text'}
                        value={value}
                        disabled={disabled}
                        onChange={(event) => onChange(event.target.value)}
                        inputProps={{ title: value || '' }}
                        variant='outlined'
                    >
                        {inputType === 'SELECT'
                            ? options.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))
                            : null}
                    </TextField>
                )}
            </Box>
        </Box>
    );
}
